import React, {useEffect, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import ModalControls from "../../common/ModalControls";
import {useNavigate, useLocation} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {genderDefault} from "../../../../defaults/Items";
import {
  onSaveAndClose,
  compareData,
  restoreFormData,
  onItemsListDeleteButtonClick,
  onCloseDetails
} from "../../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import "../../../../css/DictionaryForms.css";

const Gender = () => {
  const dataUrl = "/genders";
  const relatedItemsUrl = null;
  const defaultItem = genderDefault;
  const itemName = "płeć";
  const itemNames = "płeć";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrócona nazwa", accessor: "shortName", sortable: true, searchable: true},
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const state = {
    itemsList,
    setItems,
    currentItem,
    setCurrentItem,
    currentFormState,
    setCurrentFormState,
    defaultItem,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    }
  };

  const onSaveItemClick = async (e) => {
    e.preventDefault();

    if (!currentItem.name || !currentItem.shortName) {
      if (!currentItem.name) {
        const nameInput = document.getElementById("name");
        if (nameInput) {
          nameInput.classList.add("form-input-invalid");
          nameInput.placeholder = "ten element jest wymagany";
        }
      }

      if (!currentItem.shortName) {
        const shortNameInput = document.getElementById("shortName");
        if (shortNameInput) {
          shortNameInput.classList.add("form-input-invalid");
          shortNameInput.placeholder = "ten element jest wymagany";
        }
      }
      return;
    }

    const item = {
      id: currentItem.id,
      shortName: currentItem.shortName,
      name: currentItem.name,
      description: currentItem.description
    };

    let response;
    if (currentFormState.formAddingDataMode) {
      response = await createItem(dataUrl, item, state);
    } else {
      response = await updateItem(`${dataUrl}/${item.id}`, item, state);
    }

    if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    } else if (response.status === 409) {
      setUpdateConflict(true);
    } else {
      response && onSaveAndClose({state});
    }
  };

  const checkRelatedItems = async () => {
    setAllowDelete(true);
  };

  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };

  const restoreChanges = () => {
    setUpdateConflict(false);
    restoreFormData({state});
  };

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete]);

  useEffect(() => {
    const getData = async () => {
      const response = await getItems(dataUrl);
      if (response.status === 200) {
        setLoading(false);
        setItems(response.data);
      } else if (response.status === 401 || response.status === 403) {
        navigate('/login', {state: {from: location}, replace: true});
      } else {
        alert("Nie udało się pobrać danych.");
      }
    };
    getData();
  }, [location.key]);

  const getCompleteItem = async (id) => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response.status === 200) {
      return response.data;
    }
    if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
      return null;
    }
    alert("Nie udało się pobrać danych.");
    return null;
  };

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowa płeć",
    buttonTitle: "Dodaj płeć"
  };

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>;
  } else if (itemsList.length > 0) {
    dataSectionContent = (
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        checkRelatedItems={checkRelatedItems}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={relatedItemsUrl}
        getCompleteItem={getCompleteItem}
      />
    );
  } else {
    dataSectionContent = (
      <div className="warn_info">
        <FontAwesomeIcon className="warning-icon div-visible" icon={faExclamationCircle}/>
        &nbsp;&nbsp;&nbsp;NIE ZNALEZIONO ŻADNYCH DANYCH
      </div>
    );
  }

  const modalTitle = currentFormState.formAddingDataMode
    ? "Nowa pozycja: Płeć"
    : `Edycja: ${currentItem.name || "Płeć"}`;

  return (
    <div id="layoutSidenav_content" className="dictionary-page">
      <AppComponentCardHeader
        title="Pozycje słownika Płeć"
        buttonProps={addDataButtonProps}
      />

      <div className="container-fluid px-4 py-3">
        <div className={itemsList.length > 0 ? "card shadow p-1 mb-5 bg-white rounded-2" : "card shadow mb-5 bg-transparent rounded-2"}>
          {dataSectionContent}
        </div>
      </div>

      <Modal
        show={currentFormState.showForm}
        onHide={onClose}
        backdrop="static"
        keyboard={false}
        className="dictionary-form-modal"
        dialogClassName="dictionary-form-modal-dialog dictionary-form-modal-dialog-sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="dictionary-form-intro">
            <div>
              <strong>{currentFormState.formAddingDataMode ? "Dodajesz nową pozycję słownika" : "Edytujesz istniejącą pozycję słownika"}</strong>
              <span>Pole „Nazwa” i „Skrót” są wymagane. Skrót powinien być krótki i jednoznaczny.</span>
            </div>
            {!currentFormState.formAddingDataMode && (
              <Button size="sm" variant="outline-secondary" onClick={restoreChanges}>
                Przywróć wartości
              </Button>
            )}
          </div>

          <form id="add-gender-form" name="add-gender-form" className="dictionary-form-grid">
            <div className="dictionary-form-field">
              <label htmlFor="name"><span className="required">*</span> Nazwa</label>
              <TextInput propertyName="name" required={true} state={state}/>
              <small>Pełna nazwa widoczna użytkownikom w formularzach i tabelach.</small>
            </div>

            <div className="dictionary-form-field dictionary-form-field-short">
              <label htmlFor="shortName"><span className="required">*</span> Skrót</label>
              <TextInput propertyName="shortName" maxlen="10" required={true} state={state}/>
              <small>Maksymalnie 10 znaków.</small>
            </div>

            <div className="dictionary-form-field dictionary-form-field-full">
              <label htmlFor="description">Opis</label>
              <TextArea propertyName="description" required={false} rows="4" state={state}/>
              <small>Opcjonalne objaśnienie, do czego służy ta pozycja.</small>
            </div>
          </form>

          {updateConflict && (
            <div className="dictionary-form-error">
              <FontAwesomeIcon icon={faInfoCircle}/>
              <span>Nie można zapisać tej pozycji, ponieważ istnieje już wpis o tej nazwie lub skrócie.</span>
            </div>
          )}
        </Modal.Body>

        <ModalControls
          onDelete={onDelete}
          onCloseDetails={onClose}
          onSubmit={onSaveItemClick}
          state={state}
        />
      </Modal>

      <DeleteWarningModal
        state={state}
        onDelete={onDelete}
        deleteItemName={itemName}
      />
    </div>
  );
};

export default Gender;
